import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getMatchesByUser } from '../services/matchService';
import {
  COLLECTIONS,
  GROUPS_FIELDS,
  GROUP_MEMBERS_FIELDS,
  GROUP_MEMBER_STATUS,
  USERS_FIELDS,
  MATCHES_FIELDS,
  MATCH_STATUS,
} from '../domain/dataModel';
import { MATCH_TYPE_LABELS, getMatchTeamSides, matchTypeBadgeClass } from '../utils/matchDisplay';

export default function MyMatches() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [matches, setMatches] = useState([]);
  const [groupsMap, setGroupsMap] = useState({});
  const [membersByGroupId, setMembersByGroupId] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterGroupId, setFilterGroupId] = useState('');

  useEffect(() => {
    if (!user?.uid) {
      setMatches([]);
      setGroupsMap({});
      setMembersByGroupId({});
      setUserProfile(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [profileSnap, membersSnap] = await Promise.all([
          getDoc(doc(db, COLLECTIONS.USERS, user.uid)),
          getDocs(
            query(
              collection(db, COLLECTIONS.GROUP_MEMBERS),
              where(GROUP_MEMBERS_FIELDS.USER_ID, '==', user.uid),
              where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.ACTIVE)
            )
          ),
        ]);
        if (cancelled) return;

        const profile = profileSnap.exists() ? profileSnap.data() : null;
        setUserProfile(profile);

        const groupIds = [
          ...new Set(
            membersSnap.docs.map((d) => d.data()[GROUP_MEMBERS_FIELDS.GROUP_ID])
          ),
        ];

        if (groupIds.length === 0) {
          setGroupsMap({});
          setMembersByGroupId({});
          setMatches([]);
          setLoading(false);
          return;
        }

        const groupSnaps = await Promise.all(
          groupIds.map((id) => getDoc(doc(db, COLLECTIONS.GROUPS, id)))
        );
        if (cancelled) return;

        const gMap = {};
        groupSnaps.forEach((snap, i) => {
          if (snap.exists()) {
            gMap[groupIds[i]] = { id: snap.id, ...snap.data() };
          }
        });
        setGroupsMap(gMap);

        const memMap = {};
        await Promise.all(
          groupIds.map(async (groupId) => {
            const snap = await getDocs(
              query(
                collection(db, COLLECTIONS.GROUP_MEMBERS),
                where(GROUP_MEMBERS_FIELDS.GROUP_ID, '==', groupId),
                where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.ACTIVE)
              )
            );
            if (!cancelled) memMap[groupId] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          })
        );
        if (cancelled) return;
        setMembersByGroupId(memMap);

        const all = await getMatchesByUser(groupIds, user.uid, 50);
        if (!cancelled) setMatches(all);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Errore nel caricamento.');
          setMatches([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const filteredMatches = useMemo(() => {
    if (!filterGroupId) return matches;
    return matches.filter((m) => m.groupId === filterGroupId);
  }, [matches, filterGroupId]);

  const groupOptions = useMemo(
    () => Object.values(groupsMap).filter(Boolean),
    [groupsMap]
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-royal border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {error && (
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      )}

      <h1 className="font-display text-2xl font-bold text-brand-ice">Le mie partite</h1>

      <div className="space-y-2">
        <label htmlFor="my-matches-filter-group" className="block text-sm font-medium text-brand-ice/80">
          Gruppo
        </label>
        <select
          id="my-matches-filter-group"
          value={filterGroupId}
          onChange={(e) => setFilterGroupId(e.target.value)}
          className="flex h-10 w-full max-w-xs rounded-btn border border-surface-muted bg-surface-card px-3 py-2 font-ui text-sm text-brand-ice focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
        >
          <option value="">Tutti i gruppi</option>
          {groupOptions.map((g) => (
            <option key={g.id} value={g.id}>
              {g[GROUPS_FIELDS.NAME] || g.id}
            </option>
          ))}
        </select>
      </div>

      {filteredMatches.length === 0 ? (
        <p className="text-sm text-brand-ice/60">Nessuna partita trovata.</p>
      ) : (
        <ul className="space-y-3">
          {filteredMatches.map((m) => {
            const group = groupsMap[m.groupId];
            const groupName = group?.[GROUPS_FIELDS.NAME] || m.groupId;
            const date = m[MATCHES_FIELDS.PLAYED_AT] || m[MATCHES_FIELDS.CREATED_AT];
            const dateStr = date?.toDate?.() ? date.toDate().toLocaleDateString('it-IT') : '—';
            const score = `${m[MATCHES_FIELDS.SCORE1] ?? 0} - ${m[MATCHES_FIELDS.SCORE2] ?? 0}`;
            const typeKey = m[MATCHES_FIELDS.TYPE] || 'classic';
            const status = m[MATCHES_FIELDS.STATUS];
            const approved = status === MATCH_STATUS.APPROVED;
            const pending = status === MATCH_STATUS.PENDING_APPROVAL;
            const { left, right } = getMatchTeamSides(m, membersByGroupId, user, userProfile);
            return (
              <li key={`${m.groupId}-${m.id}`}>
                <button
                  type="button"
                  onClick={() => navigate(`/groups/${m.groupId}/matches/${m.id}`)}
                  className="w-full rounded-card border border-surface-muted bg-surface-card p-4 text-left transition-colors hover:border-brand-sky/25"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <span
                      className={`inline-flex rounded-badge border px-2.5 py-0.5 font-ui text-xs font-semibold ${matchTypeBadgeClass(typeKey)}`}
                    >
                      {MATCH_TYPE_LABELS[typeKey] || typeKey}
                    </span>
                    <span className="text-sm text-brand-ice/70">{dateStr}</span>
                  </div>
                  <div className="mb-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                    <p className="truncate text-left text-xs text-brand-ice/90 sm:text-sm">{left}</p>
                    <p className="font-display text-3xl font-bold tabular-nums text-brand-ice">{score}</p>
                    <p className="truncate text-right text-xs text-brand-ice/90 sm:text-sm">{right}</p>
                  </div>
                  <div className="flex items-center justify-between gap-2 border-t border-surface-muted pt-3 text-xs text-brand-ice/60">
                    <span className="flex items-center gap-1.5">
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${approved ? 'bg-win' : pending ? 'bg-amber-500' : 'bg-surface-muted'}`}
                        title={approved ? 'Approvata' : pending ? 'In attesa' : status || ''}
                      />
                      <span>
                        {approved ? 'Approvata' : pending ? 'In attesa di approvazione' : status || '—'}
                      </span>
                    </span>
                    <span className="truncate text-right font-medium text-brand-ice/80">{groupName}</span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
